import React, { useState } from 'react';
import {
  Button, Modal, Space, Spin, Tag, Timeline, Typography,
} from 'antd';
import { HistoryOutlined } from '@ant-design/icons';

import { useTapTerminalEvents } from '../../models/useTapTerminalEvents';
import { useTapDeviceIdentity, serialTail } from '../../models/useTapDeviceIdentity';
import { isStripeTerminalTagId } from '../../utils/deviceCategory';

const { Text } = Typography;

/** The flow log only exists for sessions after this deploy; older taps show their end-state stamps alone. */
export const TERMINAL_FLOW_LOG_SINCE = '12 Oct 2026';

/** A terminal session: a Smart POS hand-off or a kiosk answer, never a QR tap. */
export const isTerminalTap = (tap) => !!tap && (
  tap.intention === 'handoff'
  || !!tap.terminalFlowStep
  || tap.kioskSession === true
  || isStripeTerminalTagId(tap.tagId)
);

const fmt = (value) => {
  if (!value) return '—';
  const date = typeof value.toDate === 'function' ? value.toDate() : new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
};

/** Why a session ended, in plain words (session-released detail). */
const RELEASE_REASONS = {
  failed: 'failed',
  declined: 'customer declined',
  expired: 'abandoned — lock expired before the card (cleared by the sweep)',
  'stuck-payment': 'reader stuck mid-payment, intent unpaid (cleared by the sweep)',
};

/** One vocabulary for the event names the payer writes (pos-terminal-events-helper). */
export const TERMINAL_EVENT_PRESENTATION = {
  'session-minted': { color: 'green', title: (e) => `Session started (${e.stage || 'hand-off'}, by ${e.by || 'staff'})` },
  'screen-posted': { color: 'blue', title: (e) => `Screen: ${e.screen || e.stage || 'posted'}` },
  answer: { color: 'blue', title: (e) => `Reader answered: ${e.detail || 'answered'}` },
  'payment-posted': { color: 'purple', title: (e) => `Card screen posted${e.code ? ` (${e.code})` : ''}` },
  'payment-succeeded': { color: 'green', title: () => 'Payment approved' },
  'payment-failed': { color: 'red', title: (e) => `Payment failed${e.code ? `: ${e.code}` : ''}` },
  // strike-payer #391: the reader said connection_error but the INTENT says
  // paid — nothing was released; the success webhook books it.
  'payment-authorised': { color: 'green', title: (e) => `Payment authorised despite the reader${e.detail ? ` (${e.detail})` : ''}` },
  'thanks-posted': { color: 'blue', title: (e) => `Thank-you screen posted${e.detail ? ` (${e.detail})` : ''}` },
  'screen-cleared': { color: 'orange', title: (e) => `Reader refused once — screen cleared, retrying${e.detail ? ` (${e.detail})` : ''}` },
  // strike-payer #400: after an approval the reader can still be on its own "Approved" display — we wait a beat and try again.
  'thanks-busy': { color: 'orange', title: (e) => `Reader still showing Approved — waiting, then retrying the thank-you${e.detail ? ` (${e.detail})` : ''}` },
  'thanks-failed': { color: 'red', title: (e) => `Thank-you never reached the glass${e.code ? `: ${e.code}` : ''}` },
  'thanks-outcome': { color: 'green', title: (e) => `Thank-you ended: ${e.detail || 'unknown'}${e.code ? ` (${e.code})` : ''}` },
  'receipt-sent-posted': { color: 'green', title: (e) => `"Receipt sent" shown${e.detail ? ` for ${e.detail}` : ''}` },
  'session-released': { color: 'red', title: (e) => `Session released: ${RELEASE_REASONS[e.detail] || e.detail || 'failed'}` },
  rearm: { color: 'gray', title: (e) => `Standing screen requested: ${e.detail || 'unknown'}` },
};

/** The session's end-state stamps — what an old tap (pre-log) can still tell you. */
export const TerminalFlowSummary = ({ tap, device }) => {
  const unit = device && device.serial
    ? `${device.serial} (SN ${serialTail(device.serial)})${device.name ? ` · ${device.name}` : ''}`
    : (device && device.name) || '—';
  const rows = [
    ['Terminal', unit],
    ['Session', tap.kioskSession ? 'Always-on (kiosk)' : 'Hand-off'],
    ['Step reached', tap.terminalFlowStep || '—'],
    ['Thank-you', tap.terminalThanksOutcome
      ? `${tap.terminalThanksOutcome}${tap.terminalThanksFailureCode ? ` (${tap.terminalThanksFailureCode})` : ''}`
      : '—'],
    ['Tip chosen', tap.terminalChosenTipAmount != null
      ? `${(tap.terminalChosenTipAmount / 100).toFixed(2)}${tap.terminalChosenTipBp ? ` (${tap.terminalChosenTipBp / 100}%)` : ''}`
      : '—'],
    ['Fee accepted', tap.terminalFeesAccepted == null ? '—' : (tap.terminalFeesAccepted ? 'yes' : 'no')],
    ['Receipt email', tap.striker_email || '—'],
    ['Payment intent', tap.terminalPaymentIntentId || tap.paymentIntentId || '—'],
    // The reader's verdict on the card step (strike-payer #391): Stripe's
    // failure code and message, so "why" is here and not a Stripe search.
    ['Card step', tap.terminalPaymentFailureCode
      ? `${tap.terminalPaymentFailureCode}${tap.terminalPaymentFailureMessage ? ` — ${tap.terminalPaymentFailureMessage}` : ''}`
      : null],
    ['Thank-you error', tap.terminalThanksError || null],
  ].filter(([, value]) => value !== null);
  return (
    <div data-testid="terminal-flow-summary" style={{ marginBottom: '12px' }}>
      {rows.map(([label, value]) => (
        <div key={label} style={{ display: 'flex', gap: '12px', fontSize: '13px' }}>
          <Text type="secondary" style={{ minWidth: '120px' }}>{label}</Text>
          <Text>{value}</Text>
        </div>
      ))}
    </div>
  );
};

/** The live timeline, oldest first — a flow reads top-down. */
export const TerminalFlowTimeline = ({ tagId, tapId }) => {
  const [loading, events, error] = useTapTerminalEvents(tagId, tapId);
  if (loading) return <Spin size="small" />;
  if (error) return <Text type="danger">{error}</Text>;
  if (events.length === 0) {
    return (
      <Text type="secondary">
        {`No flow log for this session. The log records sessions from ${TERMINAL_FLOW_LOG_SINCE} onward; the summary above is what the tap itself carries.`}
      </Text>
    );
  }
  return (
    <Timeline pending={null} mode="left" style={{ marginTop: '8px' }}>
      {events.map((event) => {
        const presentation = TERMINAL_EVENT_PRESENTATION[event.event]
          || { color: 'gray', title: () => event.event };
        return (
          <Timeline.Item key={event.id} color={presentation.color} label={fmt(event.at)}>
            <Text strong>{presentation.title(event)}</Text>
            <Text type="secondary" style={{ display: 'block', fontSize: '11px' }}>
              {[event.stage && `stage ${event.stage}`, event.by && `by ${event.by}`].filter(Boolean).join(' · ')}
            </Text>
          </Timeline.Item>
        );
      })}
    </Timeline>
  );
};

/**
 * One line for the Admin "Tap details" modal: which unit took the money.
 * Renders nothing for non-terminal taps or while the serial is unknown.
 */
export const TerminalIdentityLine = ({ tap }) => {
  const [, device] = useTapDeviceIdentity(tap ? { tagId: tap.tagId, deviceSerial: tap.deviceSerial, deviceName: tap.deviceName } : {});
  if (!isTerminalTap(tap)) return null;
  const tail = serialTail(device.serial);
  if (!tail && !device.name) return null;
  return (
    <Space size={4} style={{ marginLeft: '8px' }}>
      <Text type="secondary">Terminal</Text>
      {tail && <Tag color="gold">{`SN ${tail}`}</Tag>}
      {device.name && <Text>{device.name}</Text>}
    </Space>
  );
};

/**
 * "Terminal flow" — Admin's view of one Smart POS session: the tap's
 * end-state stamps, then every screen, answer and outcome as it happened
 * (Charles, 12 Oct 2026: "very important for supporting terminals").
 * Renders nothing for non-terminal taps.
 */
export const TerminalFlowButton = ({ tap }) => {
  const [open, setOpen] = useState(false);
  const [, device] = useTapDeviceIdentity(tap ? { tagId: tap.tagId, deviceSerial: tap.deviceSerial, deviceName: tap.deviceName } : {});
  if (!isTerminalTap(tap)) return null;
  const tagId = tap.tagId;
  const tapId = tap.tapId || tap.id;
  const tail = serialTail(device.serial);
  return (
    <>
      <Button size="small" icon={<HistoryOutlined />} onClick={() => setOpen(true)}>Terminal flow</Button>
      <Modal
        title={(
          <Space>
            <span>Terminal flow</span>
            {tail && <Tag color="gold">{`SN ${tail}`}</Tag>}
            <Tag>{tagId}</Tag>
            <Tag>{tapId}</Tag>
          </Space>
        )}
        visible={open}
        footer={null}
        onCancel={() => setOpen(false)}
        width={720}
      >
        <TerminalFlowSummary tap={tap} device={device} />
        {open && <TerminalFlowTimeline tagId={tagId} tapId={tapId} />}
      </Modal>
    </>
  );
};

export default TerminalFlowButton;
